import { Observable, of } from 'rxjs';
import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, AfterViewChecked, NgZone, Input } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { FormComponent } from '../shared/form-component';
import { BackendService } from '../core/services/backend.service';
import { UiStateService } from '../core/services/ui-state-service';
import { GraphicsService } from '../core/services/graphics.service';
import { take, takeUntil, tap } from 'rxjs/internal/operators';
import { DorpelDto, BestellingDorpelDto } from '../shared/api-models/models';
import { BaseComponent } from '../shared/base.component';
import { ActivatedRoute } from '@angular/router';
import { DataStateService } from '../core/services/data-state.service';
import { DorpelUtils } from '../shared/models/dorpel-type.model';

const MIN_CANVAS_SIZE = 1000;

@Component({
  selector: 'luv-dorpel',
  templateUrl: './dorpel-horiz.component.html',
  styleUrls: ['./dorpel.component.scss']
})
export class DorpelComponent extends BaseComponent implements OnInit, AfterViewChecked {

  @ViewChild('dorpel_drawing') private dorpelDrawing: ElementRef<HTMLCanvasElement>;
  @Input() checkOtherFormsValidity: () => boolean;

  public formGroup: FormGroup;
  public dorpels: DorpelDto[] = [];
  private _formComponent: FormComponent;
  private _zaagmaatMismatchSignFactor: number = 0;
  public zaagmaatTargetValue: number = 0;
  public _sumOfMatenCustomError: string | null = null
  public _slidingDoorsCustomErrors: any = {
    deurmaatA: null,
    deurmaatB: null,
    deurmaatC: null,
    firstMaatA: null,
    maatC: null,
    neutValue: null
  };
  private dorpelDependentFields: string[] = ["merk", "zaagmaat", "aantal"];
  private schemaDependentFields: string[] = ["merk", "zaagmaat", "aantal", "maatA", "maatZ"];
  // private dorpelTypeDependentFields: string[] = ["aantalNeuten", "aantalSluitpotten"];
  public zaagmaatDependentFieldsEnabled: boolean = false;
  private dorpelAutodrawn: boolean = false;
  public isDrawingInfoMessageVisible: boolean = false;
  private visibleCanvasWidth: number = MIN_CANVAS_SIZE;
  private rafId: number;
  private drawTimer: ReturnType<typeof setTimeout>;
  public exampleDrawingPath: string = "";
  public get isDrawingInExampleMode(): boolean {
    return this.uiStateService.isDrawingInExampleMode();
  }

  constructor(
    private formBuilder: FormBuilder,
    private uiStateService: UiStateService,
    private dataStateService: DataStateService,
    private backend: BackendService,
    private activatedRoute: ActivatedRoute,
    private graphics: GraphicsService,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone) {
    super();
  }

  ngOnInit(): void {
    const productType = this.activatedRoute.snapshot.paramMap.get('productType');
    this.dataStateService.setHomePath(`/order-form/${productType}`);
    this.uiStateService.viewMode === "Edit" ? this.uiStateService.turnOffDrawingExample() : this.uiStateService.turnOnDrawingExample(); //in edit mode real dorpel gets autodrawn
    this.getDorpels(productType)
      .pipe(take(1))
      .pipe(tap(result => this.uiStateService.dorpelChoices = result))
      .subscribe(result => {
        this.dorpels = result;
        const currentDorpel = this.uiStateService.currentDorpel;
        this.formGroup = this.buildForm(currentDorpel);
        this._formComponent = new FormComponent(this.formGroup);

        if (this.formGroup.get('dorpel').value) {
          this.enableDorpelDependentFields();
          this.enableDorpelTypeDependentFields();
          this.enableZaagmaatDependentFields();
        }
      });

    this.uiStateService.neutCustomWidthUpdate$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((width) => {
        if (width) {
          this.draw();
        }
      });

    this.uiStateService.redraw$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((draw) => {
        if(!!draw) {
          this.draw();
        }
      });
  }

  ngAfterViewChecked() {
    if (this.dorpelAutodrawn || !this.dorpelDrawing || !this.isCanvasEmpty(this.dorpelDrawing.nativeElement)) {
      return;
    }

    this.visibleCanvasWidth = this.dorpelDrawing.nativeElement.parentElement.getBoundingClientRect().width;

    if (this.uiStateService.viewMode === 'Edit') { //when in edit mode (prefilled scenario or not), we want to autodraw the dorpel
      this.draw();
      this.cdRef.detectChanges(); //must be called after draw because drawing function triggers validation which might update the view (err message)
      this.dorpelAutodrawn = true;
    }
  }

  get isDorpelConfigurationDisabled(): boolean {
    // return this.isSlidingDorpel();
    return false;
  }

  private getDorpels(productType): Observable<DorpelDto[]> {
    const dorpelDtos: DorpelDto[] = [];
    dorpelDtos.push(<DorpelDto>{
      dorpelCode: this.uiStateService.currentDorpel.dorpelCode,
      omschrijving: this.uiStateService.currentDorpel.dorpelCode
    });
    return this.uiStateService.fromPrefilledOrder ? of(dorpelDtos) : this.backend.getDorpels(productType);
  }

  public onNeutenCountChanged(): void {
    const neutenCount = this.formGroup.get('aantalNeuten').value;
    this.uiStateService.setNeutenCount(parseInt(neutenCount));
  }

  public onSluitpottenCountChanged(): void {
    const sluitpottenCount = this.formGroup.get('aantalSluitpotten').value;
    this.uiStateService.setSluitpottenCount(sluitpottenCount);
  }

  public setupNewConfiguration(): void {
    const dorpelCode = this.formGroup.get('dorpel').value;
    const isSlidingDoor = DorpelUtils.isSlidingDoor(dorpelCode);
    const schema = isSlidingDoor ? this.formGroup.get('schema').value : "";
    this.setExampleDrawingPath(dorpelCode, schema?.toLocaleLowerCase());

    this.uiStateService.setDorpelCode(dorpelCode);
    this.uiStateService.setSchema(schema);
    this.formGroup = this.buildForm(this.uiStateService.currentDorpel);
    this.initializeDrawing();

    setTimeout(() => {
      this.enableDorpelDependentFields();
      this.enableDorpelTypeDependentFields();
      this.toggleSchemaDependentFields();
    }, 0);
  }

  private setExampleDrawingPath(dorpelCode: string, schema?: string): void {
    if (!dorpelCode) {
      this.exampleDrawingPath = "";
      return;
    }

    if (DorpelUtils.isBinnenDoor(dorpelCode)) {
      this.exampleDrawingPath = "/assets/example-drawing-bi.png";
      return;
    }

    if (DorpelUtils.isBuitenDoor(dorpelCode)) {
      this.exampleDrawingPath = "/assets/example-drawing-bu.png";
      return;
    }

    if (DorpelUtils.isSlidingDoor(dorpelCode) && schema) {
      this.exampleDrawingPath = `/assets/example-drawing-${schema}.png`;
      return;
    }

    this.exampleDrawingPath = "";
  }

  public fillZaagmaatTargetedValue(): void {
    this.formGroup.get('zaagmaat').setValue(this.zaagmaatTargetValue);
    this.uiStateService.setZaagmaat(this.zaagmaatTargetValue);
    this.draw();
  }

  //TODO: optimize redraw
  onDrawingWrapperResize() {
    this.ngZone.runOutsideAngular(() => {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      this.rafId = requestAnimationFrame(() => {
        this.visibleCanvasWidth = Math.max(this.dorpelDrawing?.nativeElement.parentElement.getBoundingClientRect().width, MIN_CANVAS_SIZE);
        this.initializeDrawing();

        if (!this.isDrawingInExampleMode) {
          this.graphics.drawDorpel(this.uiStateService.currentDorpel);
        }
      })
    });
  }

  public isSlidingDorpel(): boolean {
    const dorpelDto = this.dorpels.find(d => d.dorpelCode === this.formGroup.get('dorpel').value);
    return dorpelDto && dorpelDto.dorpelsoort === 'Hefschuifdorpel';
  }

  public showAantalNeuten(): boolean {
    return !this.isSlidingDorpel();
  }

  public showAantalSluitpotten(): boolean {
    return !this.isSlidingDorpel();
  }

  public getMaatZLabel(): string {
    const schema = this.formGroup.get('schema')?.value;
    if (!schema || schema === 'AL' || schema === 'AR') {
      return "Vak 2";
    }

    if (schema === 'C+G') {
      return "Vak 3";
    }

    return "Vak 4";
  }

  showDeurmaatA(): boolean {
    const schema = this.formGroup.get('schema')?.value;
    return schema === 'AL' || schema === 'AR' || schema === 'C+G';
  }

  showMaatA() {
    const schema = this.formGroup.get('schema')?.value;
    return schema === 'D';
  }

  showDeurmaatB() {
    const schema = this.formGroup.get('schema')?.value;
    return schema === 'D';
  }

  showMaatB() {
    const schema = this.formGroup.get('schema')?.value;
    return schema === 'C+G';
  }

  showDeurmaatC() {
    const schema = this.formGroup.get('schema')?.value;
    return schema === 'C+G';
  }

  showMaatC() {
    const schema = this.formGroup.get('schema')?.value;
    return schema === 'D';
  }

  public showError(key: string): boolean {
    if (key === 'zaagmaat') {
      let hasError = this._zaagmaatMismatchSignFactor !== 0 && !this._sumOfMatenCustomError;
      if (hasError) {
        window.scrollTo({ top: 0 });
      }
      return hasError;
    }
    return this._formComponent.showError(key);
  }

  // check if hefboom is active to hide sluitpot
  showHefdeur(): boolean {
    const schema = this.formGroup.get('schema')?.value;
    return schema === 'AL' || schema === 'AR' || schema === 'C+G' || schema === 'D';
  }


  public errorMessage(key: string): string {
    if (key === 'zaagmaat') {
      if (this._zaagmaatMismatchSignFactor === 0) return '';
      return (this._zaagmaatMismatchSignFactor < 0 ?
        ValidationMessages.zaagmaat.zaagmatAmountMismatchTooLow :
        ValidationMessages.zaagmaat.zaagmatAmountMismatchTooHigh);
    }

    const formControl = this.formGroup.get(key);
    if (formControl.valid || formControl.disabled) {
      return '';
    }
    const [firstError] = Object.keys(formControl.errors);
    const error = ValidationMessages[key] ? ValidationMessages[key][firstError] : null;
    return error || this._formComponent.errorMessage(key);
  }

  public dataIsValid(): boolean {
    const isValid = this.formGroup.valid;

    if (!isValid) {
      Object.keys(this.formGroup.controls).forEach(field => {
        const control = this.formGroup.get(field);
        control.markAsTouched();
      });
    }

    return isValid;
  }

  public getDorpelDrawingData(): string | null {
    let canvas: HTMLCanvasElement = this.dorpelDrawing.nativeElement;
    if (this.isCanvasEmpty(canvas)) {
      return null;
    }

    let imageData = canvas.toDataURL("image/png");
    return imageData;
  }

  private isCanvasEmpty(canvas: HTMLCanvasElement): boolean {
    return !canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height)
      .data.some(channel => channel !== 0);
  }

  private getNeutDimension(neutCode: string, positie: string): number {
    let neutDto = this.uiStateService.getNeutDtoByCodeAndPositie(neutCode, positie);
    if (neutDto) {
      return neutDto.neutbreedte ? parseInt(neutDto.neutbreedte) : 0;
    }

    return 0;
  }

  public validateZaagmaat(dorpel: BestellingDorpelDto): boolean {
    let total: number;
    this._zaagmaatMismatchSignFactor = 0;
    this.zaagmaatTargetValue = 0;

    if (!this.isSlidingDorpel()) {
      let maatA = dorpel.maatA || 0;
      let maatZ = dorpel.maatZ || 0;
      total = maatA + maatZ;
      total += dorpel.neuten.map(neut => {
        return (neut.customWidth || this.getNeutDimension(neut.neutCode, neut.positie)) + (neut.daagmaat || 0);
      }).reduce((accumulator, currentValue) => accumulator + currentValue);
      this.zaagmaatTargetValue = total;
    } else {
      this.resetSlidingDoorsCustomErrors();

      let neut = dorpel.neuten[0]; //single neut type for sliding doors
      let neutValue = neut && neut.neutCode ? this.getNeutDimension(neut.neutCode, neut.positie) : 0;

      if (!neutValue) { //TODO: needs refactoring (custom validation for sliding door neut missing value)
        this._slidingDoorsCustomErrors.neutValue = "Kies een neut om de tekening compleet te maken.";
        return;
      }

      let maatA = dorpel.maatA || 0;
      let maatB = dorpel.maatB || 0;
      let maatC = dorpel.maatC || 0;
      let firstMaatA = dorpel.firstMaatA || 0;
      let deurmaatA = dorpel.deurmaatA || 0;
      let deurmaatB = dorpel.deurmaatB || 0;
      let deurmaatC = dorpel.deurmaatC || 0;
      let maatZ = dorpel.maatZ || 0;
      let availableMechanismSpace = dorpel.zaagmaat - maatA - maatZ;

      switch (dorpel.schema) {
        case "AL":
        case "AR": {
          let distancesValid = this.validateALARSchemaDistances({
            availableMechanismSpace: availableMechanismSpace,
            neutValue: neutValue,
            deurmaatA: deurmaatA
          });

          if (!distancesValid) {
            return false;
          }

          total = dorpel.zaagmaat; //manually validate total
          break;
        }
        case "C+G": {
          let distancesValid = this.validateCGSchemaDistances({
            availableMechanismSpace: availableMechanismSpace,
            neutValue: neutValue,
            deurmaatA: deurmaatA,
            deurmaatC: deurmaatC,
            maatB: maatB
          });

          if (!distancesValid) {
            return false;
          }

          total = maatA + deurmaatA + maatB + deurmaatC + maatZ;
          this.zaagmaatTargetValue = total;
          break;
        }
        case "D": {
          let distancesValid = this.validateDSchema({
            availableMechanismSpace: availableMechanismSpace,
            neutValue: neutValue,
            maatA: maatA,
            maatC: maatC,
            deurmaatB: deurmaatB,
            firstMaatA: firstMaatA
          });

          if (!distancesValid) {
            return false;
          }

          total = maatA + firstMaatA + deurmaatB + maatC + maatZ;
          this.zaagmaatTargetValue = total;
          break;
        }
      }
    }

    if (total === 0) {
      this._sumOfMatenCustomError = ValidationMessages.sumOfMatenNull
    } else {
      this._sumOfMatenCustomError = null;
    }

    if (total !== dorpel.zaagmaat) {
      this._zaagmaatMismatchSignFactor = total > dorpel.zaagmaat ? -1 : 1;
    }

    return this._zaagmaatMismatchSignFactor === 0;
  }

  private resetSlidingDoorsCustomErrors(): void {
    this._slidingDoorsCustomErrors.deurmaatA = null;
    this._slidingDoorsCustomErrors.deurmaatC = null;
    this._slidingDoorsCustomErrors.firstMaatA = null;
    this._slidingDoorsCustomErrors.maatC = null;
    this._slidingDoorsCustomErrors.deurmaatB = null;
    this._slidingDoorsCustomErrors.neutValue = null;
  }

  private validateALARSchemaDistances(values: any): boolean {
    let deurmaatMinWidth = 2 * values.neutValue + 10; //10 is minimum slider length;
    let deurmaatMaxWdith = values.availableMechanismSpace - 1 * values.neutValue;

    if (values.deurmaatA < deurmaatMinWidth) {
      this._slidingDoorsCustomErrors.deurmaatA = `Minimum Deurmaat A should be ${deurmaatMinWidth}`;
      return false;
    } else if (values.deurmaatA > deurmaatMaxWdith) {
      this._slidingDoorsCustomErrors.deurmaatA = `Maximum Deurmaat A should be ${deurmaatMaxWdith}`;
      return false;
    }

    return true;
  }

  private validateCGSchemaDistances(values: any): boolean {
    let deurmaatMinWidth = 2 * values.neutValue + 10; //10 is minimum slider length;
    let deurmaatAMaxWdith = values.availableMechanismSpace - values.deurmaatC - values.maatB;
    let deurmaatCMaxWdith = values.availableMechanismSpace - values.deurmaatA - values.maatB;

    if (deurmaatAMaxWdith > deurmaatMinWidth) {
      if (values.deurmaatA < deurmaatMinWidth) {
        this._slidingDoorsCustomErrors.deurmaatA = `Minimum Deurmaat A should be ${deurmaatMinWidth}`;
        return false;
      } else if (values.deurmaatA > deurmaatAMaxWdith) {
        this._slidingDoorsCustomErrors.deurmaatA = `Maximum Deurmaat A should be ${deurmaatAMaxWdith}`;
        return false;
      }
    }

    if (deurmaatCMaxWdith > deurmaatMinWidth) {
      if (values.deurmaatC < deurmaatMinWidth) {
        this._slidingDoorsCustomErrors.deurmaatC = `Minimum Deurmaat C should be ${deurmaatMinWidth}`;
        return false;
      } else if (values.deurmaatC > deurmaatCMaxWdith) {
        this._slidingDoorsCustomErrors.deurmaatC = `Maximum Deurmaat C should be ${deurmaatCMaxWdith}`;
        return false;
      }
    }

    return true;
  }

  private validateDSchema(values: any): boolean {
    let firstMaatAMinWidth = values.neutValue //10 is minimum slider length;
    let firstMaatAMaxWidth = values.availableMechanismSpace - values.deurmaatB - values.maatC;
    let maatCMinWidth = values.neutValue //10 is minimum slider length;
    let maatCMaxWidth = values.availableMechanismSpace - values.deurmaatB - values.maatA;
    let deurmaatBMinWidth = 2 * values.neutValue;
    let deurmaatBMaxWidth = values.availableMechanismSpace - values.maatA - values.maatC;

    if (values.firstMaatA < firstMaatAMinWidth) {
      this._slidingDoorsCustomErrors.firstMaatA = `Minimum Maat A should be ${firstMaatAMinWidth}`;
      return false;
    } else if (values.firstMaatA > firstMaatAMaxWidth) {
      this._slidingDoorsCustomErrors.firstMaatA = `Maximum Maat A should be ${firstMaatAMaxWidth}`;
      return false;
    }

    if (values.maatC < maatCMinWidth) {
      this._slidingDoorsCustomErrors.maatC = `Minimum Maat C should be ${maatCMinWidth}`;
      return false;
    } else if (values.maatC > maatCMaxWidth) {
      this._slidingDoorsCustomErrors.maatC = `Maximum Maat C should be ${maatCMaxWidth}`;
      return false;
    }

    if (values.deurmaatB < deurmaatBMinWidth) {
      this._slidingDoorsCustomErrors.deurmaatB = `Minimum Deurmaat B should be ${deurmaatBMinWidth}`;
      return false;
    } else if (values.deurmaatB > deurmaatBMaxWidth) {
      this._slidingDoorsCustomErrors.deurmaatB = `Maximum Deurmaat B should be ${deurmaatBMaxWidth}`;
      return false;
    }

    return true;
  }

  private buildForm(dorpel: BestellingDorpelDto): FormGroup {
    const formGroup = this.formBuilder.group({
      dorpel: [dorpel.dorpelCode || '', [Validators.required]],
      merk: [{ value: dorpel.merk || '', disabled: true }],
      zaagmaat: [{ value: dorpel.zaagmaat || '', disabled: true }, [Validators.pattern('[0-9]+'), Validators.required]],
      aantalNeuten: [{ value: dorpel.aantalNeuten || 2, disabled: true }, [Validators.pattern('[0-9]+'), Validators.required]],
      aantalSluitpotten: [{ value: dorpel.aantalSluitpotten || 0, disabled: true }, [Validators.pattern('[0-9]+'), Validators.required]],
      schema: [dorpel.schema || ''], //TODO: field validation handling
      deurmaatA: [dorpel.deurmaatA], //TODO field validation
      deurmaatB: [dorpel.deurmaatB], //TODO field validation
      deurmaatC: [dorpel.deurmaatC],
      aantal: [{ value: dorpel.aantal || 1, disabled: true }, [Validators.min(1), Validators.required]],
      maatA: [dorpel.maatA || 0], //only for sliding doors (otherwise value is on daagmaat component/form)
      firstMaatA: [dorpel.firstMaatA],
      maatB: [dorpel.maatB],
      maatC: [dorpel.maatC],
      maatZ: [dorpel.maatZ || 0], //only for sliding doors (otherwise value is on daagmaat component/form)
      //opmerking detached to separate form handled in the dorpel page component
      //due to its position in the view
    });

    return formGroup;
  }

  public onMaatAChanged(): void {
    const maatA = this.formGroup.get('maatA').value;
    this.uiStateService.setMaatA(maatA);
  }

  public onMaatZChanged(): void {
    const maatZ = this.formGroup.get('maatZ').value;
    this.uiStateService.setMaatZ(maatZ);
  }

  public onDeurmaatAChanged(): void {
    const deurmaatA = this.formGroup.get("deurmaatA").value;
    this.uiStateService.setDeurmaatA(deurmaatA);
  }

  public onDeurmaatBChanged(): void {
    const deurmaatB = this.formGroup.get("deurmaatB").value;
    this.uiStateService.setDeurmaatB(deurmaatB);
  }

  public onFirstMaatAChanged(): void {
    const firstMaatA = this.formGroup.get("firstMaatA").value;
    this.uiStateService.setFirstMaatA(firstMaatA);
  }
  public onMaatBChanged(): void {
    const maatB = this.formGroup.get("maatB").value;
    this.uiStateService.setMaatB(maatB);
  }
  public onDeurmaatCChanged(): void {
    const deurmaatC = this.formGroup.get("deurmaatC").value;
    this.uiStateService.setDeurmaatC(deurmaatC);
  }
  public onMaatCChanged(): void {
    const maatC = this.formGroup.get("maatC").value;
    this.uiStateService.setMaatC(maatC);
  }

  private enableDorpelDependentFields(): void {
    this.dorpelDependentFields.forEach(field => {
      let fieldToEnable = this.formGroup.controls[field];

      if (fieldToEnable) {
        fieldToEnable.enable();
      }
    });
  }

  enableDorpelTypeDependentFields(): void {
    let isEditMode = this.uiStateService.viewMode === 'Edit'
    let isCurrentDoorSliding = this.isSlidingDorpel();

    if (!isEditMode) {
      this.formGroup.get('aantalNeuten').setValue(2);
      this.uiStateService.setNeutenCount(2);
    }

    if (isCurrentDoorSliding) {
      if (!isEditMode) {
        this.formGroup.get('aantalSluitpotten').setValue(0);
        this.uiStateService.setSluitpottenCount(0);
      }

      this.formGroup.controls['aantalNeuten'].disable();
      this.formGroup.controls['aantalSluitpotten'].disable();
    } else {
      this.formGroup.controls['aantalNeuten'].enable();
      this.formGroup.controls['aantalSluitpotten'].enable();
    }
  }

  toggleSchemaDependentFields(): void {
    const schema = this.formGroup.get("schema").value;
    const isSlidingDorpel = this.isSlidingDorpel();

    if (!isSlidingDorpel) {
      return;
    }

    this.schemaDependentFields.forEach(field => {
      let targetField = this.formGroup.controls[field];

      if (targetField) {
        if (!schema) {
          targetField.disable();
        } else if (schema) {
          targetField.enable();
        }
      }
    })
  }

  public enableZaagmaatDependentFields(): void {
    this.zaagmaatDependentFieldsEnabled = this.formGroup.controls.zaagmaat.value > 0;
    setTimeout(this.initializeDrawing.bind(this), 100);
  }

  private getZaagmaatMaxLimit(): number {
    return 6000;
  }

  public onZaagmaatValueChanged(): void {
    const zaagmaat = this.formGroup.get('zaagmaat').value;
    const maxLimit = this.getZaagmaatMaxLimit();

    if (!this.isSlidingDorpel() && zaagmaat > maxLimit) {
      this.formGroup.get('zaagmaat').setValue(maxLimit);
      this.uiStateService.setZaagmaat(maxLimit);
    } else {
      this.uiStateService.setZaagmaat(zaagmaat);
    }

    this.enableZaagmaatDependentFields();
  }

  public initializeDrawing(): void {
    this.graphics.initializeDrawing("dorpelDrawing", this.visibleCanvasWidth);
  }

  public draw(): void {
    if (!this.validateZaagmaat(this.uiStateService.currentDorpel) || !this.checkOtherFormsValidity()) {
      return;
    }

    this.measureSurfaceAndTriggerDebouncedDraw();
  }

  private measureSurfaceAndTriggerDebouncedDraw(): void {
    this.visibleCanvasWidth = Math.max(this.dorpelDrawing.nativeElement.parentElement.getBoundingClientRect().width, MIN_CANVAS_SIZE);
    this.initializeDrawing();
    if (this.drawTimer) {
      clearTimeout(this.drawTimer);
    }
    this.drawTimer = setTimeout(() => {
      this.graphics.drawDorpel(this.uiStateService.currentDorpel);
    }, 100);
  }
}

const ValidationMessages = {
  dorpel: {
    required: 'Selecteer een waarde a.u.b.'
  },
  zaagmaat: {
    required: 'Selecteer een waarde a.u.b.',
    zaagmatAmountMismatchTooLow: 'De zaagmaat is te kort!',
    zaagmatAmountMismatchTooHigh: 'De zaagmaat is te lang!',
  },
  aantalNeuten: {
    required: 'Selecteer een waarde a.u.b.'
  },
  aantalSluitpotten: {
    required: 'Selecteer een waarde a.u.b.'
  },
  aantal: {
    required: 'Het aantal dorpels mag niet 0 zijn',
    min: 'Het aantal dorpels mag niet 0 zijn'
  },
  sumOfMatenNull: 'Vul de deelmaten hieronder in om de dorpel te kunnen tekenen.'
};
